import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Engagement = {
  id: number
  code: string
  name: string
  account: number
  account_name: string | null
  engagement_manager: number | null
  engagement_manager_person: number | null
  engagement_manager_name: string | null
  start_date: string | null
  end_date: string | null
  currency_code: string
  total_contract_value: string
  status: 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'DELIVERED' | 'CLOSED' | 'CANCELLED'
  project_count: number
}

type Account = { id: number; name: string }

// Minimal create payload — full editing happens on the record page.
type CreateInput = {
  name: string
  account: number | ''
  engagement_manager_person: number | null
  currency_code: string
  status: Engagement['status']
}

const STATUS_COLOR: Record<Engagement['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-blue-100 text-blue-700',
  ON_HOLD: 'bg-amber-100 text-amber-700',
  DELIVERED: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-700',
}

const STATUS_FILTERS: ('ALL' | Engagement['status'])[] = [
  'ALL', 'DRAFT', 'ACTIVE', 'ON_HOLD', 'DELIVERED', 'CLOSED', 'CANCELLED',
]

export default function ConsultingEngagements() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const engagementsQ = useQuery({
    queryKey: ['consulting', 'engagements'],
    queryFn: async () => (await api.get<Engagement[]>('/consulting/engagements/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['crm', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/crm/accounts/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<{ id: number; full_name: string }[]>('/hr/persons/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStatus, setFilterStatus] = useState<'ALL' | Engagement['status']>('ALL')

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Engagement>('/consulting/engagements/', {
      name: draft.name,
      account: Number(draft.account),
      engagement_manager_person: draft.engagement_manager_person,
      currency_code: draft.currency_code,
      status: draft.status,
      // Defaults — filled in on the record page after creation.
      partner: null,
      partner_in_charge_person: null,
      opportunity: null,
      start_date: null,
      end_date: null,
      total_contract_value: '0',
      notes: '',
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['consulting', 'engagements'] })
      toast.success('Engagement created')
      setCreating(false)
      navigate(`/admin/consulting/engagements/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/engagements/${id}/`),
    onSuccess: () => {
      toast.success('Engagement deleted')
      qc.invalidateQueries({ queryKey: ['consulting', 'engagements'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const engagements = engagementsQ.data || []
  const accounts = accountsQ.data || []
  const persons = personsQ.data || []
  const filtered = filterStatus === 'ALL'
    ? engagements
    : engagements.filter((e) => e.status === filterStatus)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Engagements</h2>
          <p className="text-sm text-muted-foreground">
            {engagements.length} total · {engagements.filter((e) => e.status === 'ACTIVE').length} active
          </p>
        </div>
        <Button onClick={openCreate} disabled={accounts.length === 0}>
          <Plus className="h-3 w-3 mr-1" /> New engagement
        </Button>
      </div>

      <div className="mb-4 flex items-center space-x-2 text-sm flex-wrap">
        <span className="text-muted-foreground">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.replace('_', ' ').toLowerCase()}
          </button>
        ))}
      </div>

      {engagementsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No engagements yet. Win a CRM opportunity and click <strong>Engagement</strong> on the row to convert it,
          or create one manually with the button above.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/40">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Account</Th>
                <Th>Manager</Th>
                <Th className="text-right">Projects</Th>
                <Th>Dates</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((e) => (
                <tr
                  key={e.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/consulting/engagements/${e.id}`)}
                >
                  <Td className="font-mono text-xs">{e.code}</Td>
                  <Td className="font-medium">{e.name}</Td>
                  <Td>{e.account_name || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">{e.engagement_manager_name || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-right text-xs">{e.project_count}</Td>
                  <Td className="text-right font-mono text-xs">
                    {e.currency_code} {Number(e.total_contract_value).toFixed(2)}
                  </Td>
                  <Td className="text-xs">
                    {e.start_date || '—'} → {e.end_date || '—'}
                  </Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[e.status]}`}>
                      {e.status}
                    </span>
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/consulting/engagements/${e.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={e.project_count > 0}
                          onClick={() => e.project_count === 0 && confirm(
                            `engagement ${e.code}`,
                            () => deleteMut.mutate(e.id),
                          )}
                          className={e.project_count === 0 ? 'text-destructive' : ''}
                        >
                          {e.project_count === 0 ? 'Delete' : `Delete (${e.project_count} projects)`}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Rest filled in on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New engagement"
        description="Create the record with the essentials. Partners, dates, contract value, and notes are filled in on the engagement's record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="eng-name">Engagement name *</FieldLabel>
            <Input id="eng-name" autoFocus required value={draft.name}
              onChange={(ev) => setDraft({ ...draft, name: ev.target.value })} />
          </Field>

          <Field>
            <FieldLabel htmlFor="eng-account">Client (account) *</FieldLabel>
            <SearchableSelect
              id="eng-account"
              value={draft.account ? String(draft.account) : undefined}
              onChange={(v) => setDraft({ ...draft, account: Number(v) })}
              placeholder="— select —"
              options={accounts.map((a) => ({ value: String(a.id), label: a.name }))}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="eng-manager">Engagement manager</FieldLabel>
            <SearchableSelect
              id="eng-manager"
              value={draft.engagement_manager_person ? String(draft.engagement_manager_person) : NONE}
              onChange={(v) => setDraft({ ...draft, engagement_manager_person: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
              ]}
            />
            <FieldDescription>You can set Partner in charge from the record page.</FieldDescription>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="eng-currency">Currency</FieldLabel>
              <Input id="eng-currency" value={draft.currency_code}
                onChange={(ev) => setDraft({ ...draft, currency_code: ev.target.value.toUpperCase().slice(0, 3) })}
                className="font-mono" maxLength={3} />
            </Field>
            <Field>
              <FieldLabel htmlFor="eng-status">Status</FieldLabel>
              <Select value={draft.status}
                onValueChange={(v) => setDraft({ ...draft, status: v as Engagement['status'] })}>
                <SelectTrigger id="eng-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="ON_HOLD">On hold</SelectItem>
                  <SelectItem value="DELIVERED">Delivered</SelectItem>
                  <SelectItem value="CLOSED">Closed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    name: '',
    account: '',
    engagement_manager_person: null,
    currency_code: 'EUR',
    status: 'DRAFT',
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
